const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_VIDEO_SIZE = 100 * 1024 * 1024;

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export function validateImage(file: File): ValidationResult {
  const errors: string[] = [];

  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    errors.push(`Invalid image type: ${file.type}. Allowed: JPG, PNG, WebP`);
  }

  if (file.size > MAX_IMAGE_SIZE) {
    errors.push(`Image too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB. Max: 5MB`);
  }

  return { valid: errors.length === 0, errors };
}

export function validateVideo(file: File): ValidationResult {
  const errors: string[] = [];

  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    errors.push(`Invalid video type: ${file.type}. Allowed: MP4, WebM`);
  }

  if (file.size > MAX_VIDEO_SIZE) {
    errors.push(`Video too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB. Max: 100MB`);
  }

  return { valid: errors.length === 0, errors };
}

export function validateMedia(file: File): ValidationResult {
  if (file.type.startsWith('image/')) {
    return validateImage(file);
  }
  if (file.type.startsWith('video/')) {
    return validateVideo(file);
  }
  return { valid: false, errors: ['Unsupported file type'] };
}

export async function validateVideoCodec(file: File): Promise<ValidationResult> {
  const baseResult = validateVideo(file);
  if (!baseResult.valid) return baseResult;

  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve({ valid: true, errors: [] });
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      resolve({
        valid: false,
        errors: ['Video codec not supported. Use H.264 (MP4) or VP8/VP9 (WebM)'],
      });
    };

    video.src = URL.createObjectURL(file);
  });
}

export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

export function getVideoMetadata(file: File): Promise<{
  width: number;
  height: number;
  duration: number;
}> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };

    video.src = URL.createObjectURL(file);
  });
}
